<template>
    <v-row>
        <v-col>
            <v-row>
                <v-col>
                    <div v-if="hasFullInsurance">
                        <v-row>
                            <v-col>
                                <v-simple-table
                                    dense>
                                    <tbody>
                                        <tr>
                                            <th>{{$t("company")}}</th>
                                            <td>
                                                <span v-if="supplementaryPolicy && supplementaryPolicy.company">
                                                    {{supplementaryPolicy.company.name}}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("policy_no")}}</th>
                                            <td>
                                                {{supplementaryPolicy.policy_no}}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("start_date_time")}}</th>
                                            <td>{{formatDateTime(supplementaryPolicy.start_at)}}</td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("end_date_time")}}</th>
                                            <td>
                                                {{formatDateTime(supplementaryPolicy.end_at)}}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("coverage_value")}}</th>
                                            <td>
                                                {{formatNumber(supplementaryPolicy.coverage_value)}}
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn small class="me-2" @click="supplementaryExtendDialog = true" v-if="hasPermission(permissions.vehicle_insurance.extend)">
                                    <v-icon left>mdi-google-circles-extended</v-icon>
                                    {{$t("extend_insurance")}}
                                </v-btn>
                                <v-btn small class="error--text me-2" @click="onCancelSupplementaryInsurance" :loading="loadingSupplementaryCancel" v-if="hasPermission(permissions.vehicle_insurance.cancel)">
                                    <v-icon left>mdi-alert</v-icon>
                                    {{$t("cancel_insurance")}}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </div>
                    <div v-else-if="fetchSupplementaryPolicyLoading">
                        <v-row>
                            <v-col cols="12">
                                <v-skeleton-loader type="text" class="my-2"/>
                                <v-skeleton-loader type="text" class="my-2"/>
                                <v-skeleton-loader type="text" class="my-2"/>
                            </v-col>
                        </v-row>
                    </div>
                    <div v-else class="d-flex flex-column align-center">
                        <p class="text-h5">{{$t("supplementary_insurance")}}</p>
                        <v-alert type="warning" text dense>{{$t("no_supplementary_insurance_alert")}}</v-alert>
                        <v-btn small text @click="onNewSupplementaryInsurance" v-if="hasPermission(permissions.vehicle_insurance.create)">
                            <v-icon left>mdi-gesture-tap-button</v-icon>
                            {{$t("add_full_insurance")}}
                        </v-btn>
                    </div>
                </v-col>
                <v-col>
                    <div v-if="hasThirdPartyInsurance">
                        <v-row>
                            <v-col>
                                <v-simple-table
                                    dense>
                                    <tbody>
                                        <tr>
                                            <th>{{$t("company")}}</th>
                                            <td>
                                                <span v-if="thirdPartyPolicy && thirdPartyPolicy.company">
                                                    {{thirdPartyPolicy.company.name}}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("policy_no")}}</th>
                                            <td>
                                                {{thirdPartyPolicy.policy_no}}
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("start_date_time")}}</th>
                                            <td>{{formatDateTime(thirdPartyPolicy.start_at)}}</td>
                                        </tr>
                                        <tr>
                                            <th>{{$t("end_date_time")}}</th>
                                            <td>
                                                {{formatDateTime(thirdPartyPolicy.end_at)}}
                                            </td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-btn small class="me-2" @click="thirdPartyInsuranceExtendDialog = true" v-if="hasPermission(permissions.vehicle_insurance.extend)">
                                    <v-icon left>mdi-google-circles-extended</v-icon>
                                    {{$t("extend_insurance")}}
                                </v-btn>
                                <v-btn small class="error--text me-2" @click="onCancelThirdPartyInsurance" :loading="loadingThirdPartyPolicyCancel" v-if="hasPermission(permissions.vehicle_insurance.cancel)">
                                    <v-icon left>mdi-alert</v-icon>
                                    {{$t("cancel_insurance")}}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </div>
                    <div v-else class="d-flex flex-column align-center">
                        <p class="text-h5">{{$t("third_party_insurance")}}</p>
                        <v-alert type="error" text dense>{{$t("no_third_party_insurance_alert")}}</v-alert>
                        <v-btn small text @click="thirdPartyInsuranceDialog = true" v-if="hasPermission(permissions.vehicle_insurance.create)">
                            <v-icon left>mdi-gesture-tap-button</v-icon>
                            {{$t("add_third_party_insurance")}}
                        </v-btn>
                    </div>
                </v-col>
            </v-row>
        </v-col>

        <!-- Supplementary Insurance -->
        <v-dialog v-model="newSupplementaryDialog" max-width="600" scrollable persistent>
            <SupplementaryInsuranceCreate
                v-if="newSupplementaryDialog"
                :vehicleId="this.vehicleId"
                @cancel="newSupplementaryDialog = false"
                @created="onSupplementaryInsuranceCreated"
                />
        </v-dialog>
        <v-dialog v-model="supplementaryExtendDialog" max-width="400" scrollable persistent>
            <SupplementaryInsuranceExtend
                v-if="supplementaryExtendDialog"
                :policyId="supplementaryPolicy.id"
                @cancel="supplementaryExtendDialog = false"
                @extended="onExtendSupplementaryInsurance"/>
        </v-dialog>

        <!-- Third Party Insurance -->
        <v-dialog v-model="thirdPartyInsuranceExtendDialog" max-width="400" scrollable persistent>
            <ThirdPartyInsuranceExtend
                v-if="thirdPartyInsuranceExtendDialog"
                :policyId="thirdPartyPolicy.id"
                @cancel="thirdPartyInsuranceExtendDialog = false"
                @extended="onExtendThirdPartyInsurance"/>
        </v-dialog>
        <v-dialog v-model="thirdPartyInsuranceDialog" max-width="600" scrollable persistent>
            <ThirdPartyInsuranceCreate
                v-if="thirdPartyInsuranceDialog"
                :vehicleId="this.vehicleId"
                @cancel="thirdPartyInsuranceDialog = false"
                @created="onThirdPartyInsuranceCreated"
                />
        </v-dialog>
        <PAlert v-bind="alert" v-model="alert.display"/>
    </v-row>
</template>

<script>

import supplementary from './../../services/insurance/supplementary';
import thirdPartyPolicy from './../../services/insurance/thirdPartyInsurance';
import SupplementaryInsuranceCreate from './../../components/insurance/SupplementaryInsuranceCreate.vue';
import ThirdPartyInsuranceCreate from './../../components/insurance/ThirdPartyInsuranceCreate.vue';
import PAlert from '../../components/common/PAlert.vue';
import SupplementaryInsuranceExtend from '../../components/insurance/SupplementaryInsuranceExtend.vue';
import ThirdPartyInsuranceExtend from '../../components/insurance/ThirdPartyInsuranceExtend.vue';

import moment from 'moment';
import { formatNumber } from 'accounting'
import { mapActions } from 'vuex';
import authorize from '../../utils/authorize';

import permissions from '../../data/permissions';

export default {
    components: {
        SupplementaryInsuranceCreate,
        PAlert,
        SupplementaryInsuranceExtend,
        ThirdPartyInsuranceCreate,
        ThirdPartyInsuranceExtend
    },
    data() {
        return {
            supplementaryPolicy: {},
            thirdPartyPolicy: {},
            fetchSupplementaryPolicyLoading: false,
            fetchThirdPartyPolicyLoading: false,
            newSupplementaryDialog: false,
            thirdPartyInsuranceDialog: false,
            supplementaryExtendDialog: false,
            thirdPartyInsuranceExtendDialog: false,
            loading: false,
            loadingSupplementaryCancel: false,
            loadingThirdPartyPolicyCancel: false,
            vehicleId: null,
            alert: {
                display: false,
                content: "Are you sure?",
                callback: null
            },
            permissions
        }
    },
    computed: {
        hasFullInsurance() {
            return !!this.supplementaryPolicy.id;
        },
        hasThirdPartyInsurance() {
            return !!this.thirdPartyPolicy.id;
        },
    },
    methods: {
        ...mapActions("ui/snackbar", ["snackbar"]),
        hasPermission(code) {
            return authorize(code);
        },
        formatDateTime(datetime) {
            return moment(datetime).format("YYYY-MM-DD HH:mm")
        },
        formatNumber(n) {
            return formatNumber(n, 2)
        },
        onNewSupplementaryInsurance() {
            this.newSupplementaryDialog = true;
        },
        fetchVehicleSupplementaryPolicy() {
            this.fetchSupplementaryPolicyLoading = true;
            supplementary.getVehicleSupplementaryPolicy(this.vehicleId).then(res => {
                this.supplementaryPolicy = res.data
            })
            .catch(error => {
                if(error.response && error.response.status == 404) {
                    this.supplementaryPolicy = {}
                }
            })
            .finally(() => {
                this.fetchSupplementaryPolicyLoading = false;
            })
        },
        fetchVehicleThirdPartyPolicy() {
            this.fetchThirdPartyPolicyLoading = true;
            thirdPartyPolicy.getVehiclePolicy(this.vehicleId).then(res => {
                this.thirdPartyPolicy = res.data
            })
            .catch(error => {
                if(error.response && error.response.status == 404) {
                    this.thirdPartyPolicy = {}
                }
            })
            .finally(() => {
                this.fetchThirdPartyPolicyLoading = false;
            })
        },
        onSupplementaryInsuranceCreated() {
            this.$store.dispatch("ui/snackbar/show", {color: "success", text: "Insurance Created"})
            this.newSupplementaryDialog = false;
            this.fetchVehicleSupplementaryPolicy();
        },
        cancelSupplementaryInsurance() {
            this.loadingSupplementaryCancel = true;
            supplementary.cancel(this.supplementaryPolicy.id).then(() => {
                this
                this.fetchVehicleSupplementaryPolicy();
            }).finally(() => {
                this.loadingSupplementaryCancel = false;
            })
        },
        onCancelSupplementaryInsurance() {
            this.alert = { ...this.alert, display: true, content: this.$t("are_you_sure"), callback: this.cancelSupplementaryInsurance };
        },
        onExtendSupplementaryInsurance() {
            this.$store.dispatch("ui/snackbar/show", {color: "success", text: "Insurance Extended"})
            this.supplementaryExtendDialog = false;
            this.fetchVehicleSupplementaryPolicy();
        },
        onThirdPartyInsuranceCreated() {
            this.$store.dispatch("ui/snackbar/show", {color: "success", text: "Insurance Created"})
            this.thirdPartyInsuranceDialog = false;
            this.fetchVehicleThirdPartyPolicy();
        },
        onCancelThirdPartyInsurance() {
            this.alert = { ...this.alert, display: true, content: this.$t("are_you_sure"), callback: this.cancelThirdPartyInsurance };
        },
        cancelThirdPartyInsurance() {
            this.loadingThirdPartyPolicyCancel = true;
            thirdPartyPolicy.cancel(this.thirdPartyPolicy.id).then(() => {
                this.$store.dispatch("ui/snackbar/show", {color: "success", text: "Insurance Cancelled"})
                this.fetchVehicleThirdPartyPolicy();
            }).finally(() => {
                this.loadingThirdPartyPolicyCancel = false;
            })
        },
        onExtendThirdPartyInsurance() {
            this.$store.dispatch("ui/snackbar/show", {color: "success", text: "Insurance Extended"})
            this.thirdPartyInsuranceExtendDialog = false;
            this.fetchVehicleThirdPartyPolicy();
        }
    },
    created() {
        this.vehicleId = this.$route.params.vehicleID;
        this.fetchVehicleSupplementaryPolicy();
        this.fetchVehicleThirdPartyPolicy();
    }
}
</script>

<style>

</style>